// THIS FOR LONG FORM VIDEO PAGINATION LOAD MORE FROM BACKEND

import { useState, useRef, useEffect } from "react";
import Section from "../components/ui/Section";
import SkeletonVideo from "../components/skeleton/Skeleton";
import "../styles/watch.scss";
import VideoModal from "../components/video/VideoModal";
import { useGetChannelVideosQuery } from "../features/api/youtubeApi";
import PageHeader from "../components/PageHeader/PageHeader";

// FIXED: Added regionFilter prop with an empty string default
export default function Watch({ regionFilter = "", onClearRegion }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [activeIndex, setActiveIndex] = useState(null);
  const [shortsPage, setShortsPage] = useState(1);

  const videoMapRef = useRef(new Map());
  const [allVideos, setAllVideos] = useState([]);
  const shortsRef = useRef(null);
  const watchContainerRef = useRef(null); // Reference to auto-scroll on click

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // FIXED: Clear states and auto-scroll down if a home page region card is clicked
  useEffect(() => {
    setPage(1);
    setShortsPage(1);
    videoMapRef.current.clear();
    setAllVideos([]);

    if (regionFilter && watchContainerRef.current) {
      watchContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [regionFilter]);

  // COMBINE SEARCH TEXT OR CARD CLICK SELECTIONS TOGETHER
  const activeSearchQuery = regionFilter || searchTerm;

  const { data: shortsData } = useGetChannelVideosQuery({
    search: activeSearchQuery,
    page: shortsPage,
    limit: 20,
    isShort: true,
  });

  const { data: videosData, isLoading: videosLoading } =
    useGetChannelVideosQuery({
      search: activeSearchQuery,
      page,
      limit: 8,
      isShort: false,
    });

  const totalPages = videosData?.totalPages || 1;
  const shortsTotalPages = shortsData?.totalPages || 1;

  const shorts = allVideos.filter((v) => v.isShort);
  const longVideos = allVideos.filter((v) => !v.isShort);

  useEffect(() => {
    const container = shortsRef.current;
    if (!container) return;
    const updateArrows = () => {
      const atStart = container.scrollLeft <= 0;
      const atEnd =
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 5;
      setShowLeftArrow(!atStart);
      setShowRightArrow(!atEnd || shortsPage < shortsTotalPages);
    };
    container.addEventListener("scroll", updateArrows);
    updateArrows();
    return () => container.removeEventListener("scroll", updateArrows);
  }, [shorts, shortsPage, shortsTotalPages]);

  const scrollShorts = (dir) => {
    const container = shortsRef.current;
    if (!container) return;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({ left: dir * scrollAmount, behavior: "smooth" });
    if (dir === 1) {
      setTimeout(() => {
        const nearEnd =
          container.scrollLeft + container.clientWidth >=
          container.scrollWidth - 300;
        if (nearEnd && shortsPage < shortsTotalPages) {
          setShortsPage((prev) => prev + 1);
        }
      }, 300);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(1);
    setShortsPage(1);
    videoMapRef.current.clear();
    setAllVideos([]);
  };

  const loadMore = () => {
    if (page < totalPages) setPage(page + 1);
  };

  useEffect(() => {
    if (!videosData?.items || !shortsData?.items) return;
    let changed = false;
    const combined = [...shortsData.items, ...videosData.items];
    combined.forEach((video) => {
      if (!videoMapRef.current.has(video._id)) {
        videoMapRef.current.set(video._id, video);
        changed = true;
      }
    });
    if (changed) {
      setAllVideos(Array.from(videoMapRef.current.values()));
    }
  }, [videosData, shortsData]);

  return (
    <main className="watch-page" ref={watchContainerRef}>
      <Section>
        <PageHeader
          title="Watch"
          subtitle="Cinematic drives and quiet roads — experienced in motion."
        />
        {/* FIXED: Show clear button banner if region card filter is active, otherwise show default search bar */}
        {regionFilter ? (
          <div className="active-filter-banner">
            <p>
              Showing adventures in <span>{regionFilter}</span>
            </p>
            <button type="button" onClick={onClearRegion}>
              Clear Region Filter ✕
            </button>
          </div>
        ) : (
          <div className="watch-search">
            <input
              type="text"
              placeholder="Search videos or shorts..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        )}

        {/* ===== SHORTS ROW ===== */}
        {shorts.length > 0 && (
          <div className="watch-section watch-shorts">
            <div className="watch-section-header">
              <h3 className="watch-section-title">Shorts ({shorts.length})</h3>
              <div className="scroll-arrows">
                {showLeftArrow && (
                  <button
                    className="arrow left"
                    onClick={() => scrollShorts(-1)}
                  >
                    ◀
                  </button>
                )}
                {showRightArrow && (
                  <button
                    className="arrow right"
                    onClick={() => scrollShorts(1)}
                  >
                    ▶
                  </button>
                )}
              </div>
            </div>
            <div className="shorts-row" ref={shortsRef}>
              {shorts.map((video) => (
                <button
                  key={video._id}
                  className="short-card"
                  onClick={() =>
                    setActiveIndex(
                      allVideos.findIndex((v) => v._id === video._id)
                    )
                  }
                >
                  <img
                    src={video.thumbnails?.high?.url}
                    alt={video.title}
                    loading="lazy"
                  />
                  <span className="short-badge">SHORT</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ===== LONG VIDEOS GRID ===== */}
        <div className="watch-section">
          <h3 className="watch-section-title">Videos ({longVideos.length})</h3>
          <div className="watch-grid">
            {videosLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonVideo key={i} />
              ))}
            {longVideos.map((video) => (
              <button
                key={video._id}
                className="watch-card"
                onClick={() =>
                  setActiveIndex(
                    allVideos.findIndex((v) => v._id === video._id)
                  )
                }
              >
                <div className="watch-thumb">
                  <img
                    src={video.thumbnails?.high?.url}
                    alt={video.title}
                    loading="lazy"
                  />
                  <div className="watch-card__overlay" />
                  <div className="watch-card__content">
                    <h4>{video.title}</h4>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {page < totalPages && (
            <div className="load-more">
              <button onClick={loadMore}>Load More</button>
            </div>
          )}
        </div>
      </Section>

      {activeIndex !== null && (
        <VideoModal
          videos={allVideos}
          currentIndex={activeIndex}
          onClose={() => setActiveIndex(null)}
        />
      )}
    </main>
  );
}
